import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  getStoredUser,
  getToken,
  persistUser,
  removeToken,
  storeToken,
} from '../utils/tokenStorage';

import {
  forgotPasswordThunk,
  loginThunk,
  logoutThunk,
  resetPasswordThunk,
  signupThunk,
} from './authThunk';

import type { AuthRole } from '../types/auth';

interface AuthState {
  user: ReturnType<typeof getStoredUser>;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;

  forgotPasswordLoading: boolean;
  forgotPasswordError: string | null;
  forgotPasswordSuccess: boolean;

  resetPasswordLoading: boolean;
  resetPasswordError: string | null;
  resetPasswordSuccess: boolean;

  loginDialogOpen: boolean;
  authMode: 'login' | 'signup';
  pendingJobId: string | null;
  pendingReferralToken: string | null;
  pendingReferralUtm: ReferralUtm | null;
  requiredRole: AuthRole;
}

interface ReferralUtm {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

interface OpenLoginDialogPayload {
  mode?: 'login' | 'signup';
  jobId?: string;
  referralToken?: string;
  referralUtm?: ReferralUtm;
  requiredRole?: AuthRole;
}

const storedUser = getStoredUser();
const storedToken = getToken();

const initialState: AuthState = {
  user: storedUser,
  token: storedToken,
  isAuthenticated: Boolean(storedToken && storedUser),
  loading: false,
  error: null,

  forgotPasswordLoading: false,
  forgotPasswordError: null,
  forgotPasswordSuccess: false,

  resetPasswordLoading: false,
  resetPasswordError: null,
  resetPasswordSuccess: false,

  loginDialogOpen: false,
  authMode: 'login',
  pendingJobId: null,
  pendingReferralToken: null,
  pendingReferralUtm: null,
  requiredRole: null,
};

const authSlice = createSlice({
  name: 'auth',

  initialState,

  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    clearForgotPasswordState: (state) => {
      state.forgotPasswordLoading = false;
      state.forgotPasswordError = null;
      state.forgotPasswordSuccess = false;
    },

    clearResetPasswordState: (state) => {
      state.resetPasswordLoading = false;
      state.resetPasswordError = null;
      state.resetPasswordSuccess = false;
    },

    openLoginDialog: (
      state,
      action: PayloadAction<
        OpenLoginDialogPayload | undefined
      >
    ) => {
      state.loginDialogOpen = true;
      state.authMode =
        action.payload?.mode ?? 'login';
      state.pendingJobId =
        action.payload?.jobId ?? null;
      state.pendingReferralToken =
        action.payload?.referralToken ?? null;

      state.pendingReferralUtm =
        action.payload?.referralUtm ?? null;
      state.requiredRole =
        action.payload?.requiredRole ?? null;
      state.error = null;
    },

    closeLoginDialog: (state) => {
      state.loginDialogOpen = false;
      state.error = null;
      state.authMode = 'login';
    },

    clearPendingJob: (state) => {
      state.pendingJobId = null;
      state.pendingReferralToken = null;
      state.pendingReferralUtm = null;
    },
    setAuthMode: (
      state,
      action: PayloadAction<
        'login' | 'signup'
      >
    ) => {
      state.authMode = action.payload;
      state.error = null;
    },

    clearRequiredRole: (state) => {
      state.requiredRole = null;
    },

    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;

      state.forgotPasswordLoading = false;
      state.forgotPasswordError = null;
      state.forgotPasswordSuccess = false;

      state.resetPasswordLoading = false;
      state.resetPasswordError = null;
      state.resetPasswordSuccess = false;

      state.loginDialogOpen = false;
      state.authMode = 'login';
      state.pendingJobId = null;
      state.pendingReferralToken = null;
      state.pendingReferralUtm = null;
      state.requiredRole = null;

      removeToken();
      persistUser(null);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        loginThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.error = null;
          state.loginDialogOpen = false;

          storeToken(action.payload.token);
          persistUser(action.payload.user);
        }
      )

      .addCase(
        loginThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            action.error.message ||
            'Login failed';
        }
      )

      .addCase(signupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        signupThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.error = null;
          state.loginDialogOpen = false;

          storeToken(action.payload.token);
          persistUser(action.payload.user);
        }
      )

      .addCase(
        signupThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            action.error.message ||
            'Signup failed';
        }
      )

      .addCase(
        forgotPasswordThunk.pending,
        (state) => {
          state.forgotPasswordLoading = true;
          state.forgotPasswordError = null;
          state.forgotPasswordSuccess = false;
        }
      )

      .addCase(
        forgotPasswordThunk.fulfilled,
        (state) => {
          state.forgotPasswordLoading = false;
          state.forgotPasswordError = null;
          state.forgotPasswordSuccess = true;
        }
      )

      .addCase(
        forgotPasswordThunk.rejected,
        (state, action) => {
          state.forgotPasswordLoading = false;
          state.forgotPasswordSuccess = false;
          state.forgotPasswordError =
            action.payload ||
            action.error.message ||
            'Password reset request failed';
        }
      )

      .addCase(
        resetPasswordThunk.pending,
        (state) => {
          state.resetPasswordLoading = true;
          state.resetPasswordError = null;
          state.resetPasswordSuccess = false;
        }
      )

      .addCase(
        resetPasswordThunk.fulfilled,
        (state) => {
          state.resetPasswordLoading = false;
          state.resetPasswordError = null;
          state.resetPasswordSuccess = true;
        }
      )

      .addCase(
        resetPasswordThunk.rejected,
        (state, action) => {
          state.resetPasswordLoading = false;
          state.resetPasswordSuccess = false;
          state.resetPasswordError =
            action.payload ||
            action.error.message ||
            'Password reset failed';
        }
      )

      .addCase(logoutThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        logoutThunk.fulfilled,
        (state) => {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.loading = false;
          state.error = null;

          state.forgotPasswordLoading = false;
          state.forgotPasswordError = null;
          state.forgotPasswordSuccess = false;

          state.resetPasswordLoading = false;
          state.resetPasswordError = null;
          state.resetPasswordSuccess = false;

          state.loginDialogOpen = false;
          state.authMode = 'login';
          state.pendingJobId = null;
          state.pendingReferralToken = null;
          state.pendingReferralUtm = null;
          state.requiredRole = null;

          removeToken();
          persistUser(null);
        }
      )

      .addCase(
        logoutThunk.rejected,
        (state, action) => {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.loading = false;
          state.error =
            action.payload ||
            action.error.message ||
            'Logout failed';

          state.forgotPasswordLoading = false;
          state.forgotPasswordError = null;
          state.forgotPasswordSuccess = false;

          state.resetPasswordLoading = false;
          state.resetPasswordError = null;
          state.resetPasswordSuccess = false;

          state.loginDialogOpen = false;
          state.authMode = 'login';
          state.pendingJobId = null;
          state.pendingReferralToken = null;
          state.pendingReferralUtm = null;
          state.requiredRole = null;

          removeToken();
          persistUser(null);
        }
      );
  },
});

export const {
  clearAuth,
  clearError,
  clearForgotPasswordState,
  clearResetPasswordState,
  clearPendingJob,
  clearRequiredRole,
  closeLoginDialog,
  openLoginDialog,
  setAuthMode,
} = authSlice.actions;

export default authSlice.reducer;